/**
 * admin-billing-api.ts — Superadmin billing + AI-usage endpoints.
 *
 * Superadmin: GET  /admin/billing/tenants
 *             PATCH /admin/billing/tenants/{id}
 *             GET  /admin/billing/tenants/{id}/ai-usage?window=
 */
import { apiClient } from './index';
import type { PlanKey, BillingStatus } from '../../config/plans';

export type { PlanKey, BillingStatus };

export interface TenantBillingRow {
    id: string;
    name: string;
    slug: string;
    plan: PlanKey;
    billing_status: BillingStatus;
    /** '' when the tenant has no cycle recorded (the API COALESCEs NULL to ''). */
    billing_cycle: 'monthly' | 'annual' | '';
    is_active: boolean;
    created_at: string;
    paid_at?: string;
    trial_ends_at?: string;
    user_count: number;
    project_count: number;
    ai_calls: number;
    ai_tokens: number;
    ai_last_used?: string;
}

export type UsageWindow = '7d' | '30d' | '90d' | 'all';

export interface TenantAIUsageUser {
    user_id: string;
    email: string;
    calls: number;
    tokens: number;
    last_used?: string;
}

export interface TenantAIUsageRecent {
    user_id: string;
    email?: string;
    surface: string;
    input_tokens: number;
    output_tokens: number;
    ok: boolean;
    created_at: string;
}

export interface TenantAIUsage {
    users: TenantAIUsageUser[];
    recent: TenantAIUsageRecent[];
}

export async function getAdminBillingTenants(window: UsageWindow): Promise<TenantBillingRow[]> {
    const { data } = await apiClient.get<TenantBillingRow[]>('/admin/billing/tenants', {
        params: { window },
    });
    return data;
}

export async function patchAdminBillingTenant(
    id: string,
    patch: { plan?: PlanKey; billing_status?: BillingStatus; billing_cycle?: 'monthly' | 'annual' },
): Promise<void> {
    await apiClient.patch(`/admin/billing/tenants/${id}`, patch);
}

export async function getTenantAIUsage(id: string, window: UsageWindow): Promise<TenantAIUsage> {
    const { data } = await apiClient.get<TenantAIUsage>(`/admin/billing/tenants/${id}/ai-usage`, {
        params: { window },
    });
    return data;
}
